// src/components/dashboard/wall/FirtChallangeLeaderBoardTableComponent.jsx
import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import { motion, useReducedMotion, LayoutGroup } from "framer-motion";
import axios from "axios";

import useIndividualStream from "../../../../shared/hooks/useIndividualSocket";
import { RAW_REZA_BASE, API_ENDPOINT } from "../../../../../lib/api-url";

const DEFAULT_AVATAR = "/avatar/avatar-default.webp";
const REQUIRE_API_NAME = false;
const BASE_URL = RAW_REZA_BASE; // e.g. https://api.yourdomain.com  (no trailing slash)

// 1→60, 2→55, ..., 12→5, 13+→0
const pointsForRank = (rank) => {
  if (!Number.isFinite(rank)) return 0;
  if (rank < 1) return 0;
  if (rank === 1) return 60;
  if (rank === 2) return 55;
  const v = 60 - (rank - 1) * 5;
  return Math.max(0, v);
};

function fmt(v) {
  if (v == null || Number.isNaN(Number(v))) return "-";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

const CONTAINER_RIGHT = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18, delayChildren: 0.18 } },
};
const CONTAINER_LEFT = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18, delayChildren: 0.48 } },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, x: 56, filter: "blur(4px)" },
  show: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { type: "tween", duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ── helpers to understand multiple server shapes ───────────────────── */
function ensureObject(maybe) {
  if (!maybe) return null;
  if (typeof maybe === "string") {
    try {
      return JSON.parse(maybe);
    } catch {
      return null;
    }
  }
  return maybe;
}

/**
 * Returns either:
 *  - { kind: 'risk_event', login, equity, balance, ts }
 *  - { kind: 'snapshot', items: [{login, equity, balance, username}], ts }
 *  - null (ignore)
 */
function parseIncoming(frame) {
  const raw = ensureObject(frame) || frame || {};
  const type = raw.type || raw.event || (raw.RiskEvent ? "risk_event" : null);
  const data = raw.data ?? raw.payload ?? raw.RiskEvent ?? raw;

  if (!data) return null;

  if (type === "init" && Array.isArray(data.accounts)) {
    const items = data.accounts
      .map((a) => ({
        login: Number(a.login),
        equity: Number(a.equity),
        balance: Number(a.balance),
        username: a.username || String(a.login),
      }))
      .filter((x) => Number.isFinite(x.login));
    return {
      kind: "snapshot",
      items,
      ts: data.timestamp || raw.timestamp || Date.now(),
    };
  }

  if (
    type === "risk_event" ||
    data.login != null ||
    data.new_value != null ||
    data.corrected_value != null
  ) {
    const login = Number(data.login);
    const equity = Number(data.new_value ?? data.equity);
    let balance = Number(data.corrected_value ?? data.balance);
    if (!Number.isFinite(balance)) {
      const m = String(data.message || "").match(/Balance=\$([0-9.\-]+)/i);
      if (m) balance = Number(m[1]);
    }
    return {
      kind: "risk_event",
      login: Number.isFinite(login) ? login : null,
      equity: Number.isFinite(equity) ? equity : null,
      balance: Number.isFinite(balance) ? balance : null,
      ts: data.timestamp || raw.timestamp || Date.now(),
    };
  }

  if (type === "heartbeat" && data.login) {
    return {
      kind: "risk_event",
      login: Number(data.login),
      equity: Number(data.equity),
      balance: Number(data.balance),
      ts: data.timestamp || raw.timestamp || Date.now(),
    };
  }

  return null;
}

export default memo(function FirtChallangeLeaderBoardTableComponent({ play = true }) {
  // Live account store keyed by login
  const [accountsByLogin, setAccountsByLogin] = useState({});
  // Name cache from finalists API
  const nameCacheRef = useRef(new Map());

  // --- ENTRANCE CONTROL ---
  const entranceDoneRef = useRef(false);
  const [entranceDone, setEntranceDone] = useState(false);
  const initialRankedRef = useRef(null);

  const reduceMotion = useReducedMotion();
  const itemVariants = reduceMotion
    ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.2 } } }
    : ITEM_VARIANTS;

  /* 1) Seed display names + avatars from finalists API (prefix with BASE_URL when needed) */
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINT.users.getFinalistsData);
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        if (!items.length) return;
        setAccountsByLogin((prev) => {
          const next = { ...prev };
          for (const it of items) {
            const login = Number(it?.account_login);
            if (!Number.isFinite(login)) continue;

            const fullName = `${it?.first_name ?? ""} ${it?.last_name ?? ""}`.trim();
            const displayName = fullName || it?.username || String(login);
            nameCacheRef.current.set(login, displayName);

            const equity = Number(it?.equity);
            const balance = Number(it?.balance);

            // Build avatar URL: BASE_URL + "/" + relative path (or use default)
            const rawAvatar = it?.avatar; // e.g. "avatars/11.png" or null
            const seededAvatar =
              rawAvatar
                ? (String(rawAvatar).startsWith("http")
                    ? String(rawAvatar)
                    : `${BASE_URL}/${String(rawAvatar).replace(/^\/+/, "")}`)
                : DEFAULT_AVATAR;

            next[login] = {
              login,
              username: displayName,
              equity: Number.isFinite(equity) ? equity : next[login]?.equity ?? null,
              balance: Number.isFinite(balance) ? balance : next[login]?.balance ?? null,
              profileImage: next[login]?.profileImage || seededAvatar,
            };
          }
          return next;
        });
      } catch (e) {
        // best-effort; non-fatal
        console.error("Finalists init failed:", e);
      }
    })();
  }, []);

  /* 2) Wire your existing socket hook (no change) */
  const { lastItem } = useIndividualStream({
    enabled: true,
  });

  /* 3) Apply incoming events to the live map immediately (don’t clobber avatar) */
  useEffect(() => {
    if (!lastItem) return;
    const evt = parseIncoming(lastItem);
    if (!evt) return;

    if (evt.kind === "snapshot" && Array.isArray(evt.items)) {
      setAccountsByLogin((prev) => {
        const next = { ...prev };
        for (const it of evt.items) {
          const username =
            nameCacheRef.current.get(it.login) || it.username || String(it.login);
          next[it.login] = {
            login: it.login,
            username,
            equity: Number.isFinite(it.equity) ? it.equity : prev[it.login]?.equity ?? null,
            balance: Number.isFinite(it.balance) ? it.balance : prev[it.login]?.balance ?? null,
            profileImage: prev[it.login]?.profileImage || DEFAULT_AVATAR,
          };
        }
        return next;
      });
      return;
    }

    if (evt.kind === "risk_event" && evt.login) {
      setAccountsByLogin((prev) => {
        const old = prev[evt.login];
        const username =
          nameCacheRef.current.get(evt.login) || old?.username || String(evt.login);
        return {
          ...prev,
          [evt.login]: {
            login: evt.login,
            username,
            equity: evt.equity ?? old?.equity ?? null,
            balance: evt.balance ?? old?.balance ?? null,
            profileImage: old?.profileImage || DEFAULT_AVATAR,
          },
        };
      });
    }
  }, [lastItem]);

  /* 4) Ranking/top 20 view model */
  const rankedLive = useMemo(() => {
    const rows = Object.values(accountsByLogin)
      .map((acc) => ({
        id: acc.login,
        login: acc.login,
        username: acc.username || String(acc.login),
        equity: Number(acc.equity ?? 0),
        balance: Number(acc.balance ?? 0),
        profileImage: acc.profileImage || DEFAULT_AVATAR,
        _hasApi: nameCacheRef.current.has(acc.login),
      }))
      .filter((r) => (REQUIRE_API_NAME ? r._hasApi : true));

    rows.sort((a, b) => b.equity - a.equity);
    return rows.slice(0, 20).map((u, i) => ({
      ...u,
      rank: i + 1,
      points: pointsForRank(i + 1),
    }));
  }, [accountsByLogin]);

  // Freeze first ranked set for the entrance animation
  if (!initialRankedRef.current && rankedLive.length) {
    initialRankedRef.current = rankedLive;
  }

  const currentlyRanked = entranceDone ? rankedLive : initialRankedRef.current ?? rankedLive;

  const rightCol = currentlyRanked.slice(0, 10);
  const leftCol = currentlyRanked.slice(10, 20);

  /* 5) Entrance timing (unchanged) */
  useEffect(() => {
    if (entranceDoneRef.current) return;
    const itemDur = reduceMotion ? 0.2 : 0.9;
    const stagger = 0.18;
    const delayR = 0.18;
    const delayL = 0.48;
    const countR = rightCol.length || 10;
    const countL = leftCol.length || 10;
    const totalR = delayR + Math.max(0, countR - 1) * stagger + itemDur;
    const totalL = delayL + Math.max(0, countL - 1) * stagger + itemDur;
    const total = Math.max(totalR, totalL) + 0.2;

    const t = setTimeout(() => {
      entranceDoneRef.current = true;
      setEntranceDone(true);
    }, total * 1000);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once

  const Header = () => (
    <div className="header-component w-[400px] relative">
      <img
        src="/red-table/red-table-header.svg"
        className="w-[240px] z-[2] absolute top-[-20px] left-[100px]"
        alt=""
      />
      <div className="z-[4] right-[22%] top-[-16px] header-text-container flex items-center justify-center absolute gap-[9px]">
        <span className="text-[10px] text-[#FFFFFF] font-semibold">امتیاز</span>
        <span className="text-[10px] text-[#FFFFFF] font-semibold">موجودی‌لحظه‌ای</span>
        <span className="text-[10px] text-[#FFFFFF] font-semibold">موجودی</span>
        <span className="text-[10px] text-[#FFFFFF] font-semibold">رتبه</span>
      </div>
    </div>
  );

  // Read LIVE values inside each card to avoid stale UI post-entrance
  const Card = ({ user, isEntrancePhase }) => {
    const live = accountsByLogin[user.login] || {};
    const liveEquity = live.equity ?? user.equity;
    const liveBalance = live.balance ?? user.balance;
    const liveUsername = live.username ?? user.username;

    // Build final avatar src for render (prefix BASE_URL + "/" if relative)
    const avatarSrc = user.profileImage
      ? (user.profileImage.startsWith("http")
          ? user.profileImage
          : `${BASE_URL}/${user.profileImage.replace(/^\/+/, "")}`)
      : DEFAULT_AVATAR;

    const cardProps = isEntrancePhase
      ? { variants: itemVariants }
      : { initial: false, animate: undefined };

    return (
      <motion.div
        className="card-container relative w-[400px] z-[3]"
        {...cardProps}
        layout={isEntrancePhase ? false : true}
        layoutId={`lb-card-${user.id}`}
        transition={{ layout: { duration: 0.28, ease: [0.2, 0.8, 0.2, 1] } }}
      >
        <img src="/red-table/red-table-banner.svg" className="w-[450px]" alt="" />
        <img
          src="/red-table/red-table-sub-banner.svg"
          className="absolute right-[64px] bottom-[-8px] w-[137px]"
          alt=""
        />
        <img
          src="/red-table/red-table-user-box.svg"
          className="absolute right-0 top-[-4px] w-[83px]"
          alt=""
        />
        <img
          src={avatarSrc}
          className="w-[54px] absolute right-[16px] top-[-32px]"
          alt={liveUsername}
          loading="lazy"
          onError={(e) => {
            if (e.currentTarget.src !== DEFAULT_AVATAR) {
              e.currentTarget.src = DEFAULT_AVATAR;
            }
          }}
        />
        <div className="text-container flex items-center justify-center gap-3 absolute right-[90px] top-[10px]">
          <div className="points-container flex justify-center items-center gap-[16px] ">
            <span className="text-[#FFFFFF] text-[10px] font-bold">{fmt(user.points)}</span>
            <span className="text-[#FFFFFF] text-[10px] font-bold">{fmt(liveEquity)}</span>
            <span className="text-[#FFFFFF] text-[10px] font-bold">{fmt(liveBalance)}</span>
            <span className="text-[#FFFFFF] text-[10px] font-bold">{user.rank}</span>
          </div>
          <span className="text-[#FFFFFF] text-[10px] font-bold w-[80px] text-right truncate">
            {liveUsername}
          </span>
        </div>
      </motion.div>
    );
  };

  const columnProps = (side, isEntrancePhase) =>
    isEntrancePhase
      ? {
          initial: "hidden",
          animate: play ? "show" : "hidden",
          variants: side === "right" ? CONTAINER_RIGHT : CONTAINER_LEFT,
          layout: false,
        }
      : { initial: false, animate: undefined, variants: undefined, layout: "position" };

  const isEntrancePhase = !entranceDone;

  return (
    <LayoutGroup id="leaderboard-shared-layout">
      <div className="flex justify-center gap-[182px] mb-[32px] flex-row-reverse">
        <div>
          <Header />
          <motion.div
            className="w-full flex flex-col items-center justify-center gap-[38px]"
            {...columnProps("right", isEntrancePhase)}
          >
            {rightCol.map((u) => (
              <Card key={u.id} user={u} isEntrancePhase={isEntrancePhase} />
            ))}
          </motion.div>
        </div>

        <div>
          <Header />
          <motion.div
            className="w-full flex flex-col items-center justify-center gap-[38px]"
            {...columnProps("left", isEntrancePhase)}
          >
            {leftCol.map((u) => (
              <Card key={u.id} user={u} isEntrancePhase={isEntrancePhase} />
            ))}
          </motion.div>
        </div>
      </div>
    </LayoutGroup>
  );
});
